import {
  createColorPalette,
  finalizeColorPalette
} from '@cvent/carina/utils/themeBuilder/colorPalette';
import {
  createColorStack,
  finalizeColorStack
} from '@cvent/carina/utils/themeBuilder/colorStack';
import chroma from 'chroma-js';
import darkFormula from './formulas/darkFormula';
import defaultFormula from './formulas/defaultFormula';
import lightRailsFormula from './formulas/lightRailsFormula';
import darkRailsFormula from './formulas/darkRailsFormula';
import { presetThemes } from './allPresets';

const deriveAdjustedColors = (seedColor, darkOrLightStack) => {
  let nominalValueOfSeed;

  // eslint-disable-next-line no-restricted-syntax
  for (const nominalValue in darkOrLightStack) {
    if (
      chroma(seedColor).hex() === chroma(darkOrLightStack[nominalValue]).hex()
    ) {
      nominalValueOfSeed = nominalValue;
      break;
    }
  }

  let adjusted1 = '70';
  let adjusted2 = '100';

  if (nominalValueOfSeed === '70') {
    adjusted1 = '100';
    adjusted2 = '150';
  } else if (
    nominalValueOfSeed === '100' ||
    nominalValueOfSeed === '150' ||
    nominalValueOfSeed === 'max'
  ) {
    adjusted2 = '45';
  }

  return [adjusted1, adjusted2];
};

/**
 * Determines if a color is "light".
 * @param {string} rgbColor - The rgb value of the color to test
 *
 * @return True if the color is light, false otherwise.
 */
export const isLight = rgbColor => {
  const color = chroma(rgbColor);
  const r = color.get('rgb.r');
  const g = color.get('rgb.g');
  const b = color.get('rgb.b');
  // https://www.w3.org/TR/AERT/#color-contrast
  const o = Math.round(r * 299 + g * 587 + b * 114) / 1000;
  return o > 125;
};

/**
 * Determines if a color is white (or close to white).
 * @param {string} color - The value of the color to test
 *
 * @return true if the color is light (or close to white), false otherwise.
 */
export const isCloseToWhite = color => {
  if (chroma.valid(color)) return chroma(color).luminance() > 0.96;
  return false;
};

/**
 * Selects either whiteText or darkText based upon the provided rgb color value.
 * @param {string} rgbColor - The rgb value of the color to test.
 */
const onDarkOrLight = rgbColor => {
  const whiteText = chroma('#FFFFFF').css();
  const darkText = chroma('#1A2026').css();
  return isLight(rgbColor) ? darkText : whiteText;
};

const buildWaxStack = (stack, finalizedStack, themeName) => {
  const [adjusted1NominalValue, adjusted2NominalValue] = deriveAdjustedColors(
    stack.seedColor,
    finalizedStack[themeName]
  );
  const textOnSeed = onDarkOrLight(stack.seedColor);

  return {
    seedColor: stack.seedColor,
    adjusted1: finalizedStack[themeName][adjusted1NominalValue],
    adjusted2: finalizedStack[themeName][adjusted2NominalValue],
    stack: finalizedStack[themeName],
    textOnSeed
  };
};

const buildWaxPalette = (primaryStack, secondaryStack, additionalColor2) => {
  const finalizedPrimaryStack = finalizeColorStack(primaryStack);
  const finalizedSecondaryStack = finalizeColorStack(secondaryStack);
  const finalizedAdditionalColor2Stack = finalizeColorStack(additionalColor2);

  return {
    default: {
      primary: buildWaxStack(primaryStack, finalizedPrimaryStack, 'default'),
      secondary: buildWaxStack(
        secondaryStack,
        finalizedSecondaryStack,
        'default'
      ),
      additionalColor2: buildWaxStack(
        additionalColor2,
        finalizedAdditionalColor2Stack,
        'default'
      )
    },
    dark: {
      primary: buildWaxStack(primaryStack, finalizedPrimaryStack, 'dark'),
      secondary: buildWaxStack(secondaryStack, finalizedSecondaryStack, 'dark'),
      additionalColor2: buildWaxStack(
        additionalColor2,
        finalizedAdditionalColor2Stack,
        'dark'
      )
    }
  };
};

export const buildThemes = (
  { primary, secondary, additionalColor2 },
  formula = 'rails'
) => {
  const primaryStack =
    primary && createColorStack(primary, ['brand', 'accent', 'tertiary'], true);
  const secondaryStack =
    secondary && createColorStack(secondary, 'interactive', true);
  const additionalColor2Stack =
    additionalColor2 && createColorStack(additionalColor2, [], true);
  const carinaPalette = finalizeColorPalette(
    createColorPalette(primaryStack, secondaryStack)
  );
  const waxPalette = buildWaxPalette(
    primaryStack,
    secondaryStack,
    additionalColor2Stack
  );

  const lightThemes = {
    rails: () =>
      lightRailsFormula(finalizeColorPalette(createColorPalette()), waxPalette),
    default: () => defaultFormula(carinaPalette)
  };
  const darkThemes = {
    rails: () =>
      darkRailsFormula(finalizeColorPalette(createColorPalette()), waxPalette),
    default: () => darkFormula(carinaPalette)
  };

  const defaultCarinaTokens = lightThemes[formula]?.() || lightThemes.default();
  const darkCarinaTokens = darkThemes[formula]?.() || darkThemes.default();

  return {
    default: {
      palette: waxPalette.default,
      ...defaultCarinaTokens,
      additionalColor2
    },
    dark: {
      palette: waxPalette.dark,
      ...darkCarinaTokens,
      additionalColor2
    }
  };
};

export const buildLightTheme = (
  themeName,
  colorScheme = 'default',
  formula = 'rails'
) => {
  const colorSet = presetThemes[themeName] || {};
  if (!colorSet.primary || !colorSet.secondary) {
    return (colorScheme === 'dark' ? darkFormula : defaultFormula)(
      finalizeColorPalette(createColorPalette())
    );
  }
  return buildThemes(colorSet, formula)[colorScheme];
};
